export type CanonicalMaterialMode = 'CLIENT_MATERIALS' | 'CONTRACTOR_MATERIALS' | 'MIXED_MATERIALS' | 'INFO_MATERIALS';
export type LegacyMaterialMode = 'client' | 'contractor' | 'info';
export type MaterialMode = CanonicalMaterialMode | LegacyMaterialMode;
export type MaterialBillingMode = 'billed_to_client' | 'supplied_by_client';

import {
  calculatePercentageMoney,
  multiplyMoney,
  roundMoney,
  sumMoney,
} from './money';
import {
  calculateMaterialFinancials,
  normalizeContractorFinancialSettings,
  type PurchasePriceMode,
  type SalesVatMode,
} from './contractorFinancialSettings';

export const DEFAULT_MATERIAL_MARKUP_PERCENT = 10;
export const DEFAULT_SMALL_CONSUMABLES_PERCENT = 5;

export interface MaterialCostItem {
  id?: string;
  label?: string;
  name?: string;
  category?: string;
  quantity?: number;
  unit?: string;
  estimatedTotal?: number;
  purchaseTotal?: number;
  contractorUnitPriceNet?: number;
  billingMode?: MaterialBillingMode;
  isHelper?: boolean;
  materialStandardLabel?: string;
}

export interface MaterialClientLine {
  label: string;
  quantityLabel: string;
  clientPrice: number;
}

export interface MaterialModeSummary {
  mode: CanonicalMaterialMode;
  informationalMaterialTotal: number;
  baseMaterialTotal: number;
  helperMaterialTotal: number;
  markupTotal: number;
  smallConsumablesTotal: number;
  clientMaterialTotal: number;
  internalPurchaseTotal: number;
  internalMaterialProfit: number;
  materialCostForMargin: number;
  deductibleVat: number;
  clientMaterialPrice: number;
  pdfMaterialTotal: number;
  clientLines: MaterialClientLine[];
}

export function normalizeMaterialMode(mode: MaterialMode): CanonicalMaterialMode {
  if (mode === 'client') return 'CLIENT_MATERIALS';
  if (mode === 'contractor') return 'CONTRACTOR_MATERIALS';
  if (mode === 'info') return 'INFO_MATERIALS';
  return mode;
}

export function calculateMaterialModeSummary(input: {
  materials?: MaterialCostItem[];
  mode: MaterialMode;
  markupPercent?: number;
  smallConsumablesEnabled?: boolean;
  smallConsumablesPercent?: number;
  vatPayer?: boolean;
  purchasePriceMode?: PurchasePriceMode;
  salesVatMode?: SalesVatMode;
}): MaterialModeSummary {
  const mode = normalizeMaterialMode(input.mode);
  const materials = input.materials ?? [];
  const markupPercent = input.markupPercent ?? DEFAULT_MATERIAL_MARKUP_PERCENT;
  const smallConsumablesPercent = input.smallConsumablesPercent ?? DEFAULT_SMALL_CONSUMABLES_PERCENT;

  const billableMaterials = materials.filter((material) => isBillableMaterial(material, mode));
  const informationalMaterialTotal =
    mode === 'INFO_MATERIALS' ? sumEstimatedTotal(materials) : 0;
  const baseMaterialTotal = sumEstimatedTotal(billableMaterials);
  const helperMaterialTotal = sumEstimatedTotal(billableMaterials.filter(isHelperMaterial));
  const purchaseTotal = sumPurchaseTotal(billableMaterials);
  const markupTotal = mode === 'CONTRACTOR_MATERIALS'
    ? calculatePercentageMoney(baseMaterialTotal, markupPercent / 100)
    : 0;
  const smallConsumablesTotal = input.smallConsumablesEnabled === true
    ? calculatePercentageMoney(helperMaterialTotal, smallConsumablesPercent / 100)
    : 0;
  const clientMaterialTotal = sumMoney([baseMaterialTotal, markupTotal, smallConsumablesTotal]);
  const financials = calculateMaterialFinancials({
    settings: normalizeContractorFinancialSettings({
      vatPayer: input.vatPayer,
      purchasePriceMode: input.purchasePriceMode,
      salesVatMode: input.salesVatMode,
      materialSide:
        mode === 'CONTRACTOR_MATERIALS' || mode === 'MIXED_MATERIALS'
          ? 'contractor'
          : mode === 'INFO_MATERIALS'
            ? 'info'
            : 'client',
    }),
    purchasePrice: purchaseTotal,
    clientMaterialPrice: clientMaterialTotal,
  });
  const internalMaterialProfit = sumMoney([
    financials.clientMaterialPrice,
    -financials.materialCostForMargin,
  ]);

  return {
    mode,
    informationalMaterialTotal,
    baseMaterialTotal,
    helperMaterialTotal,
    markupTotal,
    smallConsumablesTotal,
    clientMaterialTotal,
    internalPurchaseTotal: purchaseTotal,
    internalMaterialProfit,
    materialCostForMargin: financials.materialCostForMargin,
    deductibleVat: financials.deductibleVat,
    clientMaterialPrice: financials.clientMaterialPrice,
    pdfMaterialTotal: financials.pdfMaterialTotal,
    clientLines: buildClientMaterialLines(billableMaterials, markupTotal, smallConsumablesTotal),
  };
}

export function getMaterialModePdfNote(mode: MaterialMode): string {
  const normalized = normalizeMaterialMode(mode);

  if (normalized === 'CLIENT_MATERIALS') {
    return 'Materiały po stronie klienta. Oferta obejmuje robociznę, bez doliczania materiałów.';
  }

  if (normalized === 'INFO_MATERIALS') {
    return 'Szacunkowy koszt materiałów informacyjnie. Materiały nie są wliczone w cenę usługi.';
  }

  if (normalized === 'MIXED_MATERIALS') {
    return 'Część materiałów po stronie klienta, część po stronie wykonawcy. Do ceny wchodzą tylko pozycje rozliczane z klientem.';
  }

  return 'Materiały po stronie wykonawcy. Oferta obejmuje robociznę i materiały.';
}

export function getMaterialModeLabel(mode: MaterialMode): string {
  const normalized = normalizeMaterialMode(mode);

  if (normalized === 'CLIENT_MATERIALS') return 'Materiały po stronie klienta';
  if (normalized === 'INFO_MATERIALS') return 'Materiały informacyjnie';
  if (normalized === 'MIXED_MATERIALS') return 'Materiały mieszane';
  return 'Materiały po stronie wykonawcy';
}

export function isHelperMaterial(material: MaterialCostItem): boolean {
  if (material.isHelper === true) return true;

  const text = normalizeText(`${material.id ?? ''} ${material.category ?? ''} ${material.label ?? ''} ${material.name ?? ''}`);

  return [
    'grunt',
    'primer',
    'akryl',
    'acryl',
    'silikon',
    'tasma',
    'tape',
    'folia',
    'foil',
    'walek',
    'walki',
    'pedzel',
    'pedzle',
    'roller',
    'brush',
    'chemia',
    'drobna',
    'helper',
    'accessory',
    'protection',
    'zabezpieczenia',
    'sandpaper',
    'papier',
  ].some((token) => text.includes(token));
}

function isBillableMaterial(material: MaterialCostItem, mode: CanonicalMaterialMode): boolean {
  if (mode === 'CLIENT_MATERIALS' || mode === 'INFO_MATERIALS') {
    return false;
  }

  if (mode === 'MIXED_MATERIALS') {
    return material.billingMode === 'billed_to_client';
  }

  return true;
}

function buildClientMaterialLines(
  materials: MaterialCostItem[],
  markupTotal: number,
  smallConsumablesTotal: number
): MaterialClientLine[] {
  const groups = new Map<string, { quantityLabels: string[]; clientPrice: number }>();
  const baseTotal = sumEstimatedTotal(materials);
  const markupFactor = baseTotal > 0 ? (baseTotal + markupTotal) / baseTotal : 1;

  for (const material of materials) {
    const label = getClientMaterialLabel(material);
    const current = groups.get(label) ?? { quantityLabels: [], clientPrice: 0 };
    current.quantityLabels.push(formatMaterialQuantity(material.quantity ?? 0, material.unit ?? ''));
    current.clientPrice += (material.estimatedTotal ?? 0) * markupFactor;
    groups.set(label, current);
  }

  if (smallConsumablesTotal > 0) {
    addSyntheticLine(groups, 'Materiały pomocnicze', smallConsumablesTotal);
  }

  return Array.from(groups.entries())
    .map(([label, data]) => ({
      label,
      quantityLabel: data.quantityLabels.filter(Boolean).join(' + ') || 'zestaw',
      clientPrice: roundMoney(data.clientPrice),
    }))
    .filter((line) => line.clientPrice > 0);
}

function addSyntheticLine(
  groups: Map<string, { quantityLabels: string[]; clientPrice: number }>,
  label: string,
  value: number
): void {
  const current = groups.get(label) ?? { quantityLabels: [], clientPrice: 0 };
  current.quantityLabels.push('zestaw');
  current.clientPrice += value;
  groups.set(label, current);
}

function getClientMaterialLabel(material: MaterialCostItem): string {
  const text = normalizeText(`${material.category ?? ''} ${material.label ?? ''} ${material.name ?? ''}`);

  if (text.includes('primer') || text.includes('grunt')) return 'Grunt';
  if (text.includes('paint') || text.includes('farba')) {
    return material.materialStandardLabel ?? material.label ?? material.name ?? 'Farba lateksowa standard, biała / jasny kolor';
  }
  if (text.includes('foil') || text.includes('tape') || text.includes('folia') || text.includes('tasma')) {
    return 'Folia / taśmy / zabezpieczenia';
  }

  return 'Materiały pomocnicze';
}

function formatMaterialQuantity(quantity: number, unit: string): string {
  if (!quantity || !unit) return 'zestaw';
  const rounded = Math.round(quantity * 100) / 100;
  const formatted = Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded);
  return `${formatted} ${unit}`;
}

function sumEstimatedTotal(materials: MaterialCostItem[]): number {
  return sumMoney(materials.map((material) => material.estimatedTotal ?? 0));
}

function sumPurchaseTotal(materials: MaterialCostItem[]): number {
  return sumMoney(materials.map((material) => {
    const calculatedPurchaseTotal =
      typeof material.contractorUnitPriceNet === 'number' &&
      typeof material.quantity === 'number' &&
      material.contractorUnitPriceNet > 0 &&
      material.quantity > 0
        ? multiplyMoney(material.quantity, material.contractorUnitPriceNet)
        : undefined;

    return material.purchaseTotal ?? calculatedPurchaseTotal ?? material.estimatedTotal ?? 0;
  }));
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
